/**
 * docxPageSize.ts
 * Rewrites the paper size of a DOCX template in place (in word/document.xml)
 * so docx-preview lays the content out at the user's chosen size and the
 * whole preview/PDF/print pipeline follows it — instead of merely resizing the
 * export canvas, which would crop content laid out at the template's size.
 *
 * Page sizes in WordprocessingML use twips (1/20 pt; 1440 per inch).
 *   A4 = 210×297 mm = 11906×16838   A5 = 148×210 mm = 8391×11906
 *   A6 = 105×148 mm =  5953×8391
 */

import PizZip from "pizzip";
import type { CustomPaperMm, Orientation, PaperSize } from "@/types/print";

/** Portrait width/height in twips per paper size (1/20 pt; 1440 per inch). */
export const PAPER_TWIPS: Record<PaperSize, { width: number; height: number }> = {
  A4: { width: 11906, height: 16838 },
  A5: { width: 8391, height: 11906 },
  A6: { width: 5953, height: 8391 },
  Letter: { width: 12240, height: 15840 },
  Legal: { width: 12240, height: 20160 },
  // 80mm × 297mm in twips (mm → twips: ×1440/25.4)
  Thermal80: { width: 4535, height: 16838 },
  // Placeholder — real dims supplied via customMm.
  Custom: { width: 5669, height: 8504 },
};

/** Convert custom millimetre dimensions to twips (portrait). */
export function mmToTwips(mm: number): number {
  return Math.round((mm * 1440) / 25.4);
}

export interface DocxPageSize {
  paperSize: PaperSize;
  orientation: Orientation;
  /** Custom dimensions in mm (only used when paperSize === "Custom"). */
  customMm?: CustomPaperMm;
}

/**
 * Map a paper size + orientation to a `<w:pgSz …/>` XML string. When landscape
 * the "width" of the sheet is the longer edge, so the w/h are swapped and the
 * orient attribute is set.
 */
export function pgSzXml({ paperSize, orientation, customMm }: DocxPageSize): string {
  const dim =
    paperSize === "Custom" && customMm
      ? { width: mmToTwips(customMm.width), height: mmToTwips(customMm.height) }
      : PAPER_TWIPS[paperSize];
  const landscape = orientation === "landscape";
  const w = landscape ? dim.height : dim.width;
  const h = landscape ? dim.width : dim.height;
  return `<w:pgSz w:w="${w}" w:h="${h}"${orientation === "landscape" ? ` w:orient="landscape"` : ""}/>`;
}

/**
 * True when the section's page size matches the target. Used to detect a
 * template that already carries the requested size (so we can avoid a rewrite).
 */
export function sectionMatches(
  sectPrXml: string,
  target: DocxPageSize
): boolean {
  const re = /<w:pgSz\b[^>]*\/>/;
  const m = sectPrXml.match(re);
  if (!m) return false;
  return m[0].trim() === pgSzXml(target);
}

/**
 * Adjust page margins `<w:pgMar … />` proportionally for compact/landscape paper sizes
 * so that usable printable height is not consumed by A4-default 1-inch margins.
 */
export function pgMarXml(currentMarXml: string, target: DocxPageSize): string {
  if (target.paperSize === "A4" && target.orientation === "portrait") {
    return currentMarXml;
  }

  let maxTop = 1440;
  let maxBottom = 1440;
  let maxLeft = 1440;
  let maxRight = 1440;

  if (target.paperSize === "A5") {
    if (target.orientation === "landscape") {
      maxTop = 360; // ≈6.3mm
      maxBottom = 360; // ≈6.3mm
      maxLeft = 500; // ≈8.8mm
      maxRight = 500; // ≈8.8mm
    } else {
      maxTop = 500;
      maxBottom = 500;
      maxLeft = 500;
      maxRight = 500;
    }
  } else if (target.paperSize === "A6") {
    maxTop = 300;
    maxBottom = 300;
    maxLeft = 400;
    maxRight = 400;
  } else if (target.paperSize === "Letter" || target.paperSize === "Legal") {
    // Same as A4 portrait: keep template margins unless landscape (narrow).
    if (target.orientation === "landscape") {
      maxTop = 720;
      maxBottom = 720;
      maxLeft = 720;
      maxRight = 720;
    }
  } else if (target.paperSize === "Thermal80") {
    // Receipt printers: minimal margins to maximise printable width.
    maxTop = 200;
    maxBottom = 200;
    maxLeft = 150;
    maxRight = 150;
  } else if (target.paperSize === "Custom") {
    // Custom sizes behave like compact paper: clamp margins to ~5mm.
    maxTop = 300;
    maxBottom = 300;
    maxLeft = 300;
    maxRight = 300;
  }

  return currentMarXml.replace(/w:(top|bottom|left|right|header|footer)="(\d+)"/g, (match, side, valStr) => {
    const val = parseInt(valStr, 10);
    if (side === "top" && val > maxTop) return `w:top="${maxTop}"`;
    if (side === "bottom" && val > maxBottom) return `w:bottom="${maxBottom}"`;
    if (side === "left" && val > maxLeft) return `w:left="${maxLeft}"`;
    if (side === "right" && val > maxRight) return `w:right="${maxRight}"`;
    if ((side === "header" || side === "footer") && val > 300) return `w:${side}="200"`;
    return match;
  });
}

/**
 * Replace every `<w:pgSz … />` in the document with the requested size, and
 * adapt page margins `<w:pgMar … />` if needed for compact paper sizes.
 * Returns a new buffer; the input is not mutated.
 */
export function rewriteDocxPageSize(
  fileBuffer: ArrayBuffer | Uint8Array,
  target: DocxPageSize
): Uint8Array {
  const zip = new PizZip(fileBuffer);
  const docXml = zip.files["word/document.xml"]?.asText() ?? "";
  const replacement = pgSzXml(target);

  const re = /<w:pgSz\b[^>]*\/>/g;
  let count = 0;
  let next = docXml.replace(re, () => {
    count += 1;
    return replacement;
  });

  if (count === 0) {
    throw new Error(
      "Không tìm thấy khổ giấy (<w:pgSz>) trong template nên không thể đổi khổ in."
    );
  }

  // Also optimize margins for compact sizes
  const marRe = /<w:pgMar\b[^>]*\/>/g;
  next = next.replace(marRe, (marTag) => pgMarXml(marTag, target));

  zip.file("word/document.xml", next);
  return zip.generate({
    type: "uint8array",
    mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
}

/** Convenience: configurable page size for an export, or null to keep template. */
export type PageSizeOverride = DocxPageSize | null;